import { useState, useEffect, useCallback, useRef } from 'react';
import { findDOMNode } from 'react-dom';

const useTableKeyboardNavigation = (data, colCount, externalTableRef) => {
  const [focusedCell, setFocusedCell] = useState({ rowIndex: -1, colIndex: -1 });
  const [isEditing, setIsEditing] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const cellRefs = useRef(new Map());

  let rowCount = 0;

  if (data && data.length) {
    rowCount = data.length;
  }

  const minRowIndex = -1;
  const maxRowIndex = rowCount - 1;

  const isTableReady = rowCount > 0 && colCount > 0;

  const getTableElement = useCallback(() => {
    if (!externalTableRef || !externalTableRef.current) return null;

    const element = externalTableRef.current;

    if (element && element.addEventListener) {
      return element;
    }

    try {
      const domElement = findDOMNode(element);
      if (domElement && domElement.addEventListener) {
        return domElement;
      }
    } catch (error) {
      throw new Error(error.message || 'Unable to find table element');
    }

    return null;
  }, [externalTableRef]);

  useEffect(() => {
    if (isTableReady && !isInitialized) {
      setIsInitialized(true);
    } else if (!isTableReady && isInitialized) {
      setIsInitialized(false);
      setIsEditing(false);
    }
  }, [isTableReady, isInitialized, rowCount, colCount]);

  const registerCellRef = useCallback((rowIndex, colIndex, ref) => {
    if (!isTableReady) return;

    const key = `${rowIndex}-${colIndex}`;
    if (ref) {
      cellRefs.current.set(key, ref);
    } else {
      cellRefs.current.delete(key);
    }
  }, [isTableReady]);

  const getCurrentCellRef = useCallback(() => {
    if (!isTableReady || !isInitialized) return null;

    const key = `${focusedCell.rowIndex}-${focusedCell.colIndex}`;

    return cellRefs.current.get(key);
  }, [focusedCell, isTableReady, isInitialized]);

  const isCurrentCellCheckbox = useCallback(() => {
    const cellRef = getCurrentCellRef();
    if (!cellRef) return false;

    const domElement = cellRef.current || cellRef;
    if (domElement && domElement.querySelector) {
      return !!domElement.querySelector('input[type="checkbox"]');
    }

    return false;
  }, [getCurrentCellRef]);

  const toggleCurrentCheckbox = useCallback(() => {
    const cellRef = getCurrentCellRef();
    if (!cellRef) return false;

    const domElement = cellRef.current || cellRef;
    if (domElement && domElement.click) {
      domElement.click();

      return true;
    }

    return false;
  }, [getCurrentCellRef]);

  const focusCell = useCallback(() => {
    if (!isTableReady || !isInitialized) return;

    const cellRef = getCurrentCellRef();
    if (cellRef) {
      const domElement = cellRef.current || cellRef;

      if (domElement && domElement.querySelector) {
        let focusableElement = domElement.querySelector('input, textarea, [tabindex], button');
        if (!focusableElement) {
          focusableElement = domElement;
        }

        if (focusableElement && focusableElement.focus) {
          focusableElement.focus();
          if (focusableElement.tagName === 'INPUT' && focusableElement.type === 'text') {
            focusableElement.select();
          }
        }
      }
    }
  }, [getCurrentCellRef, isTableReady, isInitialized]);

  const moveFocus = useCallback((direction) => {
    if (!isTableReady || !isInitialized || isEditing || rowCount === 0 || colCount === 0) {
      return false;
    }

    let shouldStayInTable = true;
    let newFocusedCell = null;

    setFocusedCell((prev) => {
      let { rowIndex, colIndex } = prev;

      if (direction === 'up') {
        rowIndex = Math.max(minRowIndex, rowIndex - 1);
      } else if (direction === 'down') {
        rowIndex = Math.min(maxRowIndex, rowIndex + 1);
      } else if (direction === 'left') {
        colIndex = Math.max(0, colIndex - 1);
      } else if (direction === 'right') {
        colIndex = Math.min(colCount - 1, colIndex + 1);
      } else if (direction === 'tab') {
        colIndex += 1;
        if (colIndex >= colCount) {
          colIndex = 0;
          rowIndex += 1;
          if (rowIndex > maxRowIndex) {
            shouldStayInTable = false;

            return prev;
          }
        }
      } else if (direction === 'shift-tab') {
        colIndex -= 1;
        if (colIndex < 0) {
          colIndex = colCount - 1;
          rowIndex -= 1;
          if (rowIndex < 0) {
            shouldStayInTable = false;

            return prev;
          }
        }
      } else {
        return prev;
      }

      if (rowIndex < minRowIndex || rowIndex > maxRowIndex || colIndex < 0 || colIndex >= colCount) {
        return prev;
      }

      newFocusedCell = { rowIndex, colIndex };

      return newFocusedCell;
    });

    return shouldStayInTable;
  }, [rowCount, colCount, isEditing, isTableReady, isInitialized, minRowIndex, maxRowIndex]);

  const startEditing = useCallback(() => {
    if (!isTableReady || !isInitialized || isEditing) return;

    setIsEditing(true);
    setTimeout(() => {
      focusCell();
    }, 0);
  }, [isEditing, focusCell, isTableReady, isInitialized]);
  const stopEditing = useCallback(() => {
    if (!isTableReady || !isInitialized || !isEditing) return;

    setIsEditing(false);

    setTimeout(() => {
      // Blur any active input/textarea to ensure keyboard events go to table
      if (document.activeElement && (document.activeElement.tagName === 'INPUT' || document.activeElement.tagName === 'TEXTAREA')) {
        document.activeElement.blur();
      }

      const tableElement = getTableElement();
      if (tableElement && tableElement.focus) {
        tableElement.focus({ preventScroll: true });
      }
    }, 0);
  }, [isEditing, isTableReady, isInitialized, getTableElement]);

  const handleTableKeyDown = useCallback((e) => {
    if (!isTableReady || !isInitialized) return;

    const tableElement = getTableElement();
    if (!tableElement) return;

    if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (isEditing) {
        stopEditing();
      }

      moveFocus('up');
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (isEditing) {
        stopEditing();
      }

      moveFocus('down');
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault();
      if (isEditing) {
        stopEditing();
      }
      moveFocus('left');
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      if (isEditing) {
        stopEditing();
      }

      moveFocus('right');
    } else if (e.key === 'Enter' || e.key === ' ') {
      const isCheckboxCell = isCurrentCellCheckbox();
      if (e.key === ' ' && !isCheckboxCell) {
        return;
      }

      e.preventDefault();
      if (isCheckboxCell) {
        toggleCurrentCheckbox();
      } else {
        startEditing();
      }
    } else if (e.key === 'Escape') {
      e.preventDefault();
      stopEditing();
    } else if (e.key === 'Tab') {
      e.preventDefault();

      if (isEditing) {
        stopEditing();
      }

      const direction = e.shiftKey ? 'shift-tab' : 'tab';
      const shouldStayInTable = moveFocus(direction);

      if (!shouldStayInTable) {
        const currentTableElement = getTableElement();
        if (currentTableElement) {
          currentTableElement.blur();

          const focusableSelector = 'a[href]:not([disabled]), button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"]):not([disabled])';
          const allFocusableElements = Array.from(document.querySelectorAll(focusableSelector));

          const externalFocusableElements = allFocusableElements.filter((el) => {
            if (currentTableElement.contains(el)) return false;

            const rect = el.getBoundingClientRect();
            const style = window.getComputedStyle(el);

            return rect.width > 0 && rect.height > 0 &&
              style.visibility !== 'hidden' &&
              style.display !== 'none' &&
              style.opacity !== '0';
          });

          if (externalFocusableElements.length > 0) {
            if (e.shiftKey) {
              externalFocusableElements[externalFocusableElements.length - 1].focus();
            } else {
              externalFocusableElements[0].focus();
            }
          } else {
            currentTableElement.focus({ preventScroll: true });
          }
        }
      }
    } else if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !isEditing) {
      const isCheckboxCell = isCurrentCellCheckbox();
      if (e.key === ' ' && isCheckboxCell) {
        return;
      }

      startEditing();
    }
  }, [
    moveFocus,
    startEditing,
    stopEditing,
    isTableReady,
    isInitialized,
    getTableElement,
    isEditing,
    isCurrentCellCheckbox,
    toggleCurrentCheckbox,
  ]);
  const handleCellKeyDown = useCallback((e) => {
    if (!isTableReady || !isInitialized) return;

    if (e.key === 'Enter') {
      e.preventDefault();
      stopEditing();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      stopEditing();
    } else if (e.key === 'Tab') {
      e.preventDefault();
      stopEditing();

      const direction = e.shiftKey ? 'shift-tab' : 'tab';
      const shouldStayInTable = moveFocus(direction);

      if (!shouldStayInTable) {
        const currentTableElement = getTableElement();
        if (currentTableElement) {
          currentTableElement.blur();

          const focusableSelector = 'a[href]:not([disabled]), button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"]):not([disabled])';
          const allFocusableElements = Array.from(document.querySelectorAll(focusableSelector));

          const externalFocusableElements = allFocusableElements.filter((el) => {
            if (currentTableElement.contains(el)) return false;

            const rect = el.getBoundingClientRect();
            const style = window.getComputedStyle(el);

            return rect.width > 0 && rect.height > 0 &&
              style.visibility !== 'hidden' &&
              style.display !== 'none' &&
              style.opacity !== '0';
          });

          if (externalFocusableElements.length > 0) {
            if (e.shiftKey) {
              externalFocusableElements[externalFocusableElements.length - 1].focus();
            } else {
              externalFocusableElements[0].focus();
            }
          } else {
            currentTableElement.focus({ preventScroll: true });
          }
        }
      }
    } else if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
      let allowNavigation = false;
      if (e.target.selectionStart === 0) {
        allowNavigation = true;
      } else if (e.target.value && e.target.selectionStart === e.target.value.length) {
        allowNavigation = true;
      }

      if (allowNavigation) {
        e.preventDefault();
        stopEditing();
        moveFocus(e.key === 'ArrowUp' ? 'up' : 'down');
      }
    }
  }, [stopEditing, moveFocus, isTableReady, isInitialized, getTableElement]);

  useEffect(() => {
    if (!isTableReady || !isInitialized) return;

    const tableElement = getTableElement();
    if (tableElement && tableElement.addEventListener) {
      tableElement.addEventListener('keydown', handleTableKeyDown);

      return () => {
        tableElement.removeEventListener('keydown', handleTableKeyDown);
      };
    }
  }, [handleTableKeyDown, isTableReady, isInitialized, getTableElement]);

  useEffect(() => {
    if (!isTableReady || !isInitialized || isEditing) return;

    const tableElement = getTableElement();
    if (tableElement && document.activeElement && tableElement.contains(document.activeElement)) {
      const timeoutId = setTimeout(() => {
        if (tableElement && tableElement.focus) {
          tableElement.focus({ preventScroll: true });
        }
      }, 0);

      return () => clearTimeout(timeoutId);
    }
  }, [focusedCell, isEditing, isTableReady, isInitialized, getTableElement]);

  useEffect(() => {
    if (!isTableReady || !isInitialized) return;

    const tableElement = getTableElement();
    if (!tableElement) return;

    const handleTableFocus = () => {
      // Only set initial focus position if we don't have a valid focused cell yet
      if (!isEditing && document.activeElement === tableElement && focusedCell.rowIndex === -1 && focusedCell.colIndex === -1) {
        setFocusedCell({ rowIndex: 0, colIndex: 0 });
      }
    };

    tableElement.addEventListener('focus', handleTableFocus);

    return () => {
      tableElement.removeEventListener('focus', handleTableFocus);
    };
  }, [isTableReady, isInitialized, getTableElement, isEditing, focusedCell.rowIndex, focusedCell.colIndex]);

  useEffect(() => {
    if (!isTableReady) {
      cellRefs.current.clear();
    }
  }, [isTableReady]);

  const isCellActive = useCallback((rowIndex, colIndex) => {
    if (!isTableReady || !isInitialized) return false;

    return focusedCell.rowIndex === rowIndex && focusedCell.colIndex === colIndex;
  }, [focusedCell.rowIndex, focusedCell.colIndex, isTableReady, isInitialized]);

  const isCellEditing = useCallback((rowIndex, colIndex) => {
    if (!isTableReady || !isInitialized) return false;

    return isEditing && focusedCell.rowIndex === rowIndex && focusedCell.colIndex === colIndex;
  }, [isEditing, focusedCell.rowIndex, focusedCell.colIndex, isTableReady, isInitialized]);

  let returnFocusedCell = { rowIndex: 0, colIndex: 0 };
  let returnIsEditing = false;

  if (isInitialized) {
    returnFocusedCell = focusedCell;
    returnIsEditing = isEditing;
  }

  return {
    focusedCell: returnFocusedCell,
    isEditing: returnIsEditing,
    isInitialized,
    isTableReady,
    setFocusedCell,
    setIsEditing,
    registerCellRef,
    handleCellKeyDown,
    moveFocus,
    startEditing,
    stopEditing,
    isCellActive,
    isCellEditing,
    debugInfo: {
      rowCount,
      colCount,
      isTableReady,
      isInitialized,
      cellRefsCount: cellRefs.current.size,
      hasTableElement: !!getTableElement(),
    },
  };
};

export default useTableKeyboardNavigation;
